import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  IconButton, Tooltip, Paper, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import { Add, PlayArrow, CheckCircle, Schedule, Delete, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const statusColor: Record<string, any> = {
  scheduled: 'default', running: 'info', completed: 'success', postponed: 'warning', cancelled: 'error',
};

const MillingSchedule: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [postponeId, setPostponeId] = useState<string | null>(null);
  const [postponeDate, setPostponeDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await millingApi.getMillingSchedule(params);
      setRows(res.data ?? res);
      setTotal(res.total ?? (res.data ?? res).length);
    } catch { setError('Failed to load schedule'); }
    finally { setLoading(false); }
  }, [statusFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleStart = async (id: string) => {
    try { await millingApi.startMillingScheduleEntry(id); load(); }
    catch { setError('Start failed'); }
  };

  const handleComplete = async (id: string) => {
    try { await millingApi.completeMillingScheduleEntry(id); load(); }
    catch { setError('Complete failed'); }
  };

  const handlePostpone = async () => {
    if (!postponeId) return;
    try { await millingApi.postponeMillingScheduleEntry(postponeId, postponeDate); load(); }
    catch { setError('Postpone failed'); }
    finally { setPostponeId(null); setPostponeDate(''); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this schedule entry?')) return;
    try { await millingApi.deleteMillingScheduleEntry(id); load(); }
    catch { setError('Delete failed'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Milling Schedule ({total})</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/milling/schedule/new')}>
          Schedule Entry
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="running">Running</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="postponed">Postponed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" label="From Date" type="date" value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="To Date" type="date" value={dateTo}
          onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="outlined" onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); }}>
          Clear
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Order #</TableCell>
                <TableCell>Recipe</TableCell>
                <TableCell>Mill</TableCell>
                <TableCell>Planned Start</TableCell>
                <TableCell>Planned End</TableCell>
                <TableCell>Actual Start</TableCell>
                <TableCell>Actual End</TableCell>
                <TableCell align="right">Qty (MT)</TableCell>
                <TableCell align="center">Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={13} align="center">No schedule entries found</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.scheduleDate}</TableCell>
                  <TableCell>{r.orderNumber || '-'}</TableCell>
                  <TableCell>{r.recipeName || '-'}</TableCell>
                  <TableCell>{r.millName || r.millId || '-'}</TableCell>
                  <TableCell>{r.plannedStartTime || '-'}</TableCell>
                  <TableCell>{r.plannedEndTime || '-'}</TableCell>
                  <TableCell>{r.actualStartTime ? new Date(r.actualStartTime).toLocaleTimeString() : '-'}</TableCell>
                  <TableCell>{r.actualEndTime ? new Date(r.actualEndTime).toLocaleTimeString() : '-'}</TableCell>
                  <TableCell align="right">{r.plannedInputQtyMt ?? '-'}</TableCell>
                  <TableCell align="center">
                    <Chip label={r.priority} size="small"
                      color={r.priority <= 2 ? 'error' : r.priority === 3 ? 'warning' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip label={r.status} size="small" color={statusColor[r.status] ?? 'default'} />
                  </TableCell>
                  <TableCell>{r.assignedTo || '-'}</TableCell>
                  <TableCell>
                    {r.status === 'scheduled' && (
                      <Tooltip title="Start">
                        <IconButton size="small" color="success" onClick={() => handleStart(r.id)}>
                          <PlayArrow fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {r.status === 'running' && (
                      <Tooltip title="Complete">
                        <IconButton size="small" color="primary" onClick={() => handleComplete(r.id)}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(r.status === 'scheduled' || r.status === 'postponed') && (
                      <Tooltip title="Postpone">
                        <IconButton size="small" onClick={() => { setPostponeId(r.id); setPostponeDate(r.scheduleDate); }}>
                          <Schedule fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => navigate(`/milling/schedule/${r.id}/edit`)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Postpone dialog */}
      <Dialog open={!!postponeId} onClose={() => setPostponeId(null)}>
        <DialogTitle>Postpone to New Date</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField label="New Date" type="date" fullWidth size="small" value={postponeDate}
            onChange={(e) => setPostponeDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostponeId(null)}>Cancel</Button>
          <Button variant="contained" onClick={handlePostpone}>Postpone</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MillingSchedule;
